import _ from 'lodash';

import {
  recordRumDurationVital,
  recordRumError,
} from '../../util/datadog/datadogRum';
import {getVideoPlaybackErrorMessage} from './getVideoPlaybackErrorMessage';
import {type Tech, type VideoJsPlayerWithQualitySelector} from './types';

enum VideoPlayingVital {
  Playing = 'video-playing',
  Buffering = 'video-buffering',
}

// Events to track on the player
// Documentation: https://docs.videojs.com/player#events
export function handleVideoEvents({
  player,
  trackSuccess,
  trackError,
  setHasFailedToLoadStream,
  onPlaybackFailed,
  hlsSrc,
  downloadUrl,
  mimeType,
  onDecodedDimensions,
}: {
  player: VideoJsPlayerWithQualitySelector;
  trackSuccess?: (
    duration: number,
    bytesTransferred: number | undefined
  ) => void;
  trackError?: () => void;
  setHasFailedToLoadStream: (hasFailedToLoadStream: boolean) => void;
  /** Called when playback fails with no further fallback (progressive / post-HLS). */
  onPlaybackFailed?: (message: string) => void;
  hlsSrc: string | null | undefined;
  downloadUrl: string | null;
  mimeType: string;
  onDecodedDimensions?: (dimensions: {width: number; height: number}) => void;
}) {
  const context = {
    playerID: player.id(),
    hlsSrc,
    downloadUrl,
    duration: player.duration(),
    mimeType,
  };

  // One HLS → progressive fallback per player; further errors are terminal.
  let fellBackFromHls = false;

  // Vitals are measured with local performance.now() marks and recorded
  // retrospectively via recordRumDurationVital (start/stop vitals are disallowed:
  // backgrounded views skew their timing). Mirrors Datadog start/stop
  // semantics: re-starting overwrites the mark, stopping without a start is a
  // no-op.
  const vitalStartMarks = new Map<VideoPlayingVital, number>();
  const startVital = (vital: VideoPlayingVital) => {
    vitalStartMarks.set(vital, performance.now());
  };
  const stopVital = (vital: VideoPlayingVital) => {
    const mark = vitalStartMarks.get(vital);
    if (mark == null) {
      return;
    }
    vitalStartMarks.delete(vital);
    recordRumDurationVital(vital, {
      startTime: performance.timeOrigin + mark,
      duration: performance.now() - mark,
      context,
    });
  };

  player.on('error', function () {
    trackError?.();
    const mediaError = player.error();
    const errorMessage = mediaError?.message;
    const errorCode = mediaError?.code;

    // Reuse the existing RUM error name; isTerminalFailure distinguishes HLS
    // fallback from progressive / post-fallback failures.
    const isHlsFallback = hlsSrc != null && !fellBackFromHls;
    if (isHlsFallback) {
      fellBackFromHls = true;
      setHasFailedToLoadStream(true);
    } else {
      onPlaybackFailed?.(getVideoPlaybackErrorMessage(mediaError ?? null));
    }
    recordRumError('streaming-error-fallback-to-original-video', {
      ...context,
      errorMessage,
      errorCode,
      isTerminalFailure: !isHlsFallback,
    });
    stopVital(VideoPlayingVital.Playing);
  });

  // Report the video's intrinsic (decoded) dimensions once metadata is available.
  // Video metadata has no pixel dimensions, so this is the panel's only source of
  // a video's real size.
  if (onDecodedDimensions) {
    player.on('loadedmetadata', () => {
      const width = player.videoWidth();
      const height = player.videoHeight();
      if (width > 0 && height > 0) {
        onDecodedDimensions({width, height});
      }
    });
  }

  // By default, the full screen button is focused when clicked, and clicking the progress bar doesn't unfocus it.
  // This makes the progress control always focus the video so that the step hotkeys will work as expected.
  const progressControl = player.controlBar?.progressControl;
  progressControl?.on?.('click', () => {
    player.focus();
  });
  progressControl?.seekBar?.on?.('click', () => {
    player.focus();
  });

  if (trackSuccess) {
    // This event is fired when the video is ready to play. Autoplay will trigger shortly after this event.
    player.on('canplay', () => {
      // HAX: To get an upper bound on the initial chunk loaded size.
      // - It's an approximation since we're depending on the 'canplay' event trigger to fire immediately. But
      //   multiple chunks may have already loaded at this point, so it's just an upper bound approximation.
      // - We are relying on internal implementation details of video.js that aren't exposed in the types.
      //   So, we have to use a type assertion to access these properties without a typescript error.
      const tech = player?.tech(true) as Tech; // from player.d.ts, adding any argument silences the warning about directly accessing the tech. We're only using it for logging metadata to datadog, so should be okay.
      const bytesTransferred = _.has(
        tech,
        'vhs.stats.mediaBytesTransferred' // defined at https://github.com/videojs/http-streaming/blob/main/src/videojs-http-streaming.js#L971
      ) // just double checking that the property exists in case the type assertion was actually wrong.
        ? (tech?.vhs?.stats?.mediaBytesTransferred ?? 0)
        : 0;

      trackSuccess(player?.duration() ?? 0, bytesTransferred);
    });
  }

  player.on('playing', () => {
    startVital(VideoPlayingVital.Playing);
    // If the video is playing, that means we are not blocked by buffering
    stopVital(VideoPlayingVital.Buffering);
  });

  // Fires when the player is removed from the DOM (e.g. during virtualization).
  player.on('dispose', () => {
    // All vitals should be stopped when the player is disposed
    stopVital(VideoPlayingVital.Playing);
    stopVital(VideoPlayingVital.Buffering);
  });

  player.on('ended', () => {
    stopVital(VideoPlayingVital.Playing);
    // If somehow the video is buffering but we make it to the end (maybe via seeking?),
    // then we're done buffering anyway.
    stopVital(VideoPlayingVital.Buffering);
  });

  player.on('pause', () => {
    stopVital(VideoPlayingVital.Playing);
    // If the user intentionally paused the video, that means we are not blocked by buffering
    stopVital(VideoPlayingVital.Buffering);
  });

  player.on('seeking', () => {
    // The playing event should be re-triggered shortly after the seeking event, so we should stop the playing vital.
    stopVital(VideoPlayingVital.Playing);
    // The waiting event may or may not be triggered when we are seeking
    // (depending on if the chunk has been already loaded or not), so we should not start a buffering vital
  });

  // Fires when the browser is trying to get media data, but data is not available.
  player.on('stalled', () => {
    stopVital(VideoPlayingVital.Playing);
    // If data is not available at all, I wouldn't consider that a buffering event, so we should stop any existing buffering vital.
    stopVital(VideoPlayingVital.Buffering);
  });

  // A readyState change on the DOM element has caused playback to stop - see https://docs.videojs.com/player#readyState.
  // This mostly corresponds to buffering.
  player.on('waiting', () => {
    stopVital(VideoPlayingVital.Playing);
    // This means buffering is starting
    startVital(VideoPlayingVital.Buffering);
  });
}
